import { LanguagesEnum } from '@/api/enums';
import {
  CATALOGUE_READ_ONLY_PREVIEW_PARAM,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import {
  PHISHING_BOOKING_URL_DK,
  PHISHING_BOOKING_URL_EN,
} from '@/common/constants/external-urls';
import { LanguageContext } from '@/common/context/LanguageContext';
import {
  PhishingIntroCards,
  ProofPointBanner,
} from '@/components/campaigns/intro';
import heroImg from '@/images/blank-pages/phishing-intro-hero.png';
import AppButton from '@/ui/buttons/AppButton';
import { ArrowRightIcon } from '@/utils/helpers/icons.helper';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledIntroCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  min-height: 100%;
  background: var(--beige-main);
  border-radius: var(--medium-border-radius);
  font-family: var(--font-family);
  letter-spacing: normal;
`;

const StyledHeroImage = styled.img`
  display: block;
  flex-shrink: 0;
  width: min(520px, calc(100% * 520 / 466));
  height: auto;
  margin: max(-20px, calc(100% * -20 / 466)) max(-24px, calc(100% * -24 / 466))
    max(-28.5px, calc(100% * -28.5 / 466)) max(-30px, calc(100% * -30 / 466));
`;

const StyledInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--default-padding-small);
  width: 100%;
  max-width: 818px;
`;

const StyledIntroBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--medium-padding);
  max-width: 620px;
  text-align: center;

  h2 {
    margin: 0;
    font-weight: 700;
    font-size: var(--heading-semi-large-font-size);
    line-height: 1.5;
  }

  p {
    margin: 0;
    font-weight: 400;
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);

    strong {
      font-weight: 600;
    }
  }
`;

const StyledCtaBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--medium-padding);

  .p-button {
    min-width: min(302px, 100%);
    max-width: 100%;
    white-space: normal;
    justify-content: center;
    font-weight: 500;
    font-size: var(--small-font-size);

    svg {
      order: 1;
      flex-shrink: 0;
      margin-left: var(--xsmall-padding);
    }
  }

  p {
    margin: 0;
    text-align: center;
    font-weight: 500;
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);

    span {
      font-weight: 600;
    }
  }
`;

const StyledDemoLink = styled.a`
  color: inherit;
  text-decoration: underline;
  text-decoration-color: var(--red-main);
  text-decoration-thickness: 8.5%;
  text-decoration-skip-ink: none;
  text-underline-offset: 4px;
`;

const PhishingIntroPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);

  return (
    <StyledIntroCard style={{ padding: '80px 0 90px', gap: '50px' }}>
      <StyledHeroImage
        src={heroImg}
        width={1040}
        height={499}
        alt={t('phishing.intro.hero.alt')}
      />
      <StyledIntroBlock>
        <h2>{t('phishing.intro.heading')}</h2>
        <p
          dangerouslySetInnerHTML={{
            __html: t('phishing.intro.description'),
          }}
        />
      </StyledIntroBlock>
      <StyledInfoSection>
        <PhishingIntroCards />
        <ProofPointBanner />
      </StyledInfoSection>
      <StyledCtaBlock>
        <AppButton
          label={t('phishing.intro.cta.catalogue')}
          icon={<ArrowRightIcon />}
          iconPos="right"
          onClick={() =>
            navigate(
              `${RedirectPaths[
                RedirectPathsEnum.CAMPAIGNS_CATALOGUE_V2
              ]()}?${CATALOGUE_READ_ONLY_PREVIEW_PARAM}=true`,
            )
          }
        />
        <p>
          <span>{t('phishing.intro.demo.prefix')}</span>{' '}
          <StyledDemoLink
            href={
              language === LanguagesEnum.DK
                ? PHISHING_BOOKING_URL_DK
                : PHISHING_BOOKING_URL_EN
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('phishing.intro.demo.link')}
          </StyledDemoLink>
        </p>
      </StyledCtaBlock>
    </StyledIntroCard>
  );
};

export { PhishingIntroPage };
export default PhishingIntroPage;
